// src/lib/i18n.ts — Lightweight i18n system (no external dependencies)
import en from '@/locales/en'
import es from '@/locales/es'
import zh from '@/locales/zh'
import ko from '@/locales/ko'
import type { TranslationKey } from '@/locales/en'

export type Locale = 'en' | 'es' | 'zh' | 'ko'

export const SUPPORTED_LOCALES: Locale[] = ['en', 'es', 'zh', 'ko']

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  es: 'Espanol',
  zh: '\u4e2d\u6587',
  ko: '\ud55c\uad6d\uc5b4',
}

export const LOCALE_FLAGS: Record<Locale, string> = {
  en: '\ud83c\uddfa\ud83c\uddf8',
  es: '\ud83c\uddea\ud83c\uddf8',
  zh: '\ud83c\udde8\ud83c\uddf3',
  ko: '\ud83c\uddf0\ud83c\uddf7',
}

export const DEFAULT_LOCALE: Locale = 'en'

const translations: Record<Locale, Record<TranslationKey, string>> = {
  en,
  es,
  zh,
  ko,
}

/**
 * Translate a key for the given locale.
 * Falls back to English if the key is missing in the target locale.
 */
export function t(key: TranslationKey, locale: Locale = DEFAULT_LOCALE): string {
  const dict = translations[locale]
  if (dict && key in dict) {
    return dict[key]
  }
  // Fallback to English
  return en[key] ?? key
}

/** Cookie name used to persist locale preference */
export const LOCALE_COOKIE = 'locale'

/** localStorage key used to persist locale preference */
export const LOCALE_STORAGE_KEY = 'locale'

/** Check if a string is a valid Locale */
export function isValidLocale(value: unknown): value is Locale {
  return typeof value === 'string' && SUPPORTED_LOCALES.includes(value as Locale)
}

/**
 * Read the preferred locale from a cookie string (for SSR).
 * Returns DEFAULT_LOCALE if not found or invalid.
 */
export function getLocaleFromCookies(cookieString: string): Locale {
  const match = cookieString.match(new RegExp(`(?:^|;)\\s*${LOCALE_COOKIE}=([^;]+)`))
  const value = match?.[1]
  return isValidLocale(value) ? value : DEFAULT_LOCALE
}

/**
 * Set locale cookie in the browser.
 * max-age = 1 year, path = /, SameSite=Lax
 */
export function setLocaleCookie(locale: Locale): void {
  document.cookie = `${LOCALE_COOKIE}=${locale};path=/;max-age=31536000;SameSite=Lax`
}

/** Get all translation keys (useful for completeness checks in tests) */
export function getTranslationKeys(): TranslationKey[] {
  return Object.keys(en) as TranslationKey[]
}

/** Get translations for a given locale */
export function getTranslations(locale: Locale): Record<TranslationKey, string> {
  return translations[locale]
}

export type { TranslationKey }
